import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { AmplifyLogo } from "@/components/AmplifyLogo";
import { Eye, EyeOff, LogIn, Loader2 } from "lucide-react";

export const LoginForm = ({ onSwitchToRegister, onSwitchToForgotPassword }) => {
  const { login, error, setError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const result = await login(email, password);
    setIsLoading(false);

    if (!result.success) {
      // Error is already set by login function
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {/* Logo & Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <AmplifyLogo size={40} />
              <span className="text-2xl font-semibold font-['Outfit'] text-slate-900 tracking-tight">Amplify HR</span>
            </div>
            <h1 className="text-xl font-semibold text-slate-800">Welcome back</h1>
            <p className="text-sm text-slate-500 mt-1">Sign in to access the Pricing Engine</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600" data-testid="login-error">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@amplifyhrm.com"
                required
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c68ff] focus:ring-offset-1"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-slate-700">Password</label>
                {onSwitchToForgotPassword && (
                  <button
                    type="button"
                    onClick={onSwitchToForgotPassword}
                    className="text-xs text-[#3c68ff] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full h-11 rounded-lg border border-slate-200 bg-white px-4 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#3c68ff] focus:ring-offset-1"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={isLoading}
              className="w-full h-11 bg-[#3c68ff] text-white rounded-lg text-sm font-semibold hover:bg-[#2b4bca] transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn size={16} />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Don't have an account?{" "}
              <button
                data-testid="switch-to-register"
                onClick={onSwitchToRegister}
                className="text-[#3c68ff] font-medium hover:underline"
              >
                Register
              </button>
            </p>
          </div>
        </div>

        {/* Footer Note */}
        <p className="text-xs text-slate-400 text-center mt-6">
          Registration is limited to @amplifyhrm.com email addresses
        </p>
      </div>
    </div>
  );
};
